import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useToast } from '@/hooks/use-toast';
import { createDeliveryAddress, DeliveryAddressCreateRequest } from '@/services/partnerApi';
import { getStates, State } from '@/services/resourcesApi';
import { AddressMap } from './AddressMap';
import { useIsMobile } from '@/hooks/use-mobile';

interface AddDeliveryAddressFormProps {
  onSuccess: () => void;
}

const MOROCCO_COUNTRY_ID = 136; // Morocco country ID

export function AddDeliveryAddressForm({ onSuccess }: AddDeliveryAddressFormProps) {
  const { token } = useAuth();
  const { toast } = useToast();
  const [states, setStates] = useState<State[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const isMobile = useIsMobile();

  const form = useForm<DeliveryAddressCreateRequest>({
    defaultValues: {
      name: '',
      street: '',
      city: '',
      state_id: 0,
      zip_code: '',
      country_id: MOROCCO_COUNTRY_ID,
      partner_latitude: 33.5731, // Default to Casablanca coordinates
      partner_longitude: -7.5898,
    },
  });

  useEffect(() => {
    const loadStates = async () => {
      try {
        const response = await getStates(MOROCCO_COUNTRY_ID);
        setStates(response.items);
      } catch (error) {
        console.error('Failed to load states:', error);
        toast({
          title: "Erreur",
          description: "Impossible de charger les régions du Maroc.",
          variant: "destructive",
        });
      }
    };
    loadStates();
  }, []);

  const onSubmit = async (data: DeliveryAddressCreateRequest) => {
    if (!token) return;

    setLoading(true);
    try {
      await createDeliveryAddress(token, data);
      toast({
        title: "Adresse ajoutée",
        description: "L'adresse de livraison a été ajoutée avec succès.",
      });
      onSuccess();
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible d'ajouter l'adresse. Le nom pourrait déjà exister.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLocationChange = (lat: number, lng: number) => {
    form.setValue('partner_latitude', lat);
    form.setValue('partner_longitude', lng);
  };

  const validateStep1 = async () => {
    const isValid = await form.trigger(['name', 'street', 'city', 'state_id', 'zip_code']);
    return isValid;
  };

  const handleNext = async () => {
    const isValid = await validateStep1();
    if (isValid) {
      setCurrentStep(2);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(1);
  };

  return (
    <Form {...form}>
      <div className="space-y-6">
        {/* Step Indicator */}
        <div className="flex items-center justify-center space-x-4 mb-8">
          <div className={`flex items-center ${currentStep === 1 ? 'text-primary' : 'text-muted-foreground'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${currentStep === 1 ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground'}`}>
              1
            </div>
            <span className="ml-2 text-sm font-medium">Informations</span>
          </div>
          <div className={`w-12 h-0.5 ${currentStep === 2 ? 'bg-primary' : 'bg-muted'}`}></div>
          <div className={`flex items-center ${currentStep === 2 ? 'text-primary' : 'text-muted-foreground'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${currentStep === 2 ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground'}`}>
              2
            </div>
            <span className="ml-2 text-sm font-medium">Localisation</span>
          </div>
        </div>

        {/* Step 1: Form Fields */}
        {currentStep === 1 && (
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              rules={{ required: "Le nom est requis" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nom *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Ex: Bureau principal" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="street"
              rules={{ required: "L'adresse est requise" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Adresse *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Rue, avenue, numéro..." />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="city"
                rules={{ required: "La ville est requise" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Ville *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Casablanca" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="state_id"
                rules={{ required: "La région est requise" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Région *</FormLabel>
                    <Select onValueChange={(value) => field.onChange(parseInt(value))} value={field.value?.toString()}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Région" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {states.map((state) => (
                          <SelectItem key={state.id} value={state.id.toString()}>
                            {state.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="zip_code"
                rules={{ required: "Le code postal est requis" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Code postal *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="20000" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div>
              <label className="text-sm font-medium">Pays</label>
              <Input value="Maroc" disabled className="bg-muted" />
            </div>

            <div className="flex justify-end pt-4">
              <Button type="button" onClick={handleNext}>
                Suivant
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Map */}
        {currentStep === 2 && (
          <div className="space-y-4">
            <div className="min-h-[400px]">
              <h3 className="text-lg font-medium mb-4">Sélectionnez la localisation exacte</h3>
              <AddressMap
                latitude={form.watch('partner_latitude')}
                longitude={form.watch('partner_longitude')}
                onLocationChange={handleLocationChange}
              />
            </div>

            <div className="flex justify-between pt-4">
              <Button type="button" variant="outline" onClick={handlePrevious}>
                Précédent
              </Button>
              <Button type="submit" onClick={form.handleSubmit(onSubmit)} disabled={loading}>
                {loading ? "Ajout..." : "Ajouter l'adresse"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Form>
  );
}